/**
 * Pure derivations from a build view: composer state, waiting sentence,
 * approval marks, the chips under the latest assistant message (including
 * taxonomy option and concept chips while browsing, the pending decisions'
 * picker and skip chips, and the primary "Start building" chip), and the
 * pending decisions themselves.
 */

import {
  BRIEF_FIELDS,
  DECISION_KEYS,
  isPart,
  isUploadedFileSource,
  type BriefField,
  type BuildAction,
  type BuildView,
  type ConceptOptionsPart,
  type DecisionKey,
  type MessageView,
  type OutlineSnapshotPart,
  type TaxonomyOptionsPart,
} from '../../api/authorWithAiApi';
import { snapshotTarget } from './outlineDiff';
import { CHIP_LABELS, COMPOSER_COPY, DECISION_COPY, OUTLINE_COPY, SKIP_CHIP_LABELS, WAITING_COPY } from './authorWithAiCopy';

export interface ComposerState {
  enabled: boolean;
  /** Footer shown while the composer is disabled. */
  reason: string | null;
}

export function composerState(view: BuildView): ComposerState {
  switch (view.status) {
    case 'discarded': return { enabled: false, reason: COMPOSER_COPY.discarded };
    case 'completed': return { enabled: false, reason: COMPOSER_COPY.completed };
    case 'failed': return { enabled: false, reason: COMPOSER_COPY.failed };
    case 'building': return { enabled: false, reason: COMPOSER_COPY.building };
    default:
      return view.execution.active ? { enabled: false, reason: COMPOSER_COPY.turnActive } : { enabled: true, reason: null };
  }
}

/** The active conversation turn while planning, if any (drives the waiting card). */
export function activeTurn(view: BuildView) {
  const active = view.execution.active;
  return view.status === 'planning' && active?.kind === 'conversation' ? active : null;
}

const courseApprovalCurrent = (view: BuildView, revision?: number): boolean => {
  const outline = view.workingState.outline;
  return Boolean(outline && view.workingState.review.approvals.some((approval) =>
    approval.scope === 'course' && approval.targetId === outline.id && approval.targetRevision === (revision ?? outline.revision)));
};

export function waitingSentence(view: BuildView): string {
  if (isBrowsingForAnchor(view)) return WAITING_COPY.browse;
  if (isBrowsingForVideo(view)) return WAITING_COPY.browseVideo;
  if (isUploadedFileSource(view.source) && !view.workingState.outline) return WAITING_COPY.reading(view.source.unitCount, view.source.unit);
  const outline = view.workingState.outline;
  if (!outline) return WAITING_COPY.outline;
  if (view.workingState.review.mode === 'detailed' && outline.lessons.length > 1 && courseApprovalCurrent(view)
    && outline.lessons.some((lesson) => lesson.blocks.length === 0)) {
    return WAITING_COPY.blocks;
  }
  return WAITING_COPY.generic;
}

/** "4.2 MB" from a megabyte up, else "12 KB". */
export function formatBytes(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

/** The file tile's second line: "4.2 MB · 38 slides". */
export function attachmentMeta(part: { sizeBytes: number; unit: string; unitCount: number }): string {
  const unit = part.unitCount === 1 ? part.unit.replace(/s$/, '') : part.unit;
  return `${formatBytes(part.sizeBytes)} · ${part.unitCount} ${unit}`;
}

/** Section numbers as ranges: [1..9, 12] becomes "1–9, 12". */
export function formatSectionRange(numbers: readonly number[]): string {
  const sorted = [...new Set(numbers)].sort((a, b) => a - b);
  const ranges: string[] = [];
  let start = sorted[0];
  let previous = sorted[0];
  for (const number of sorted.slice(1)) {
    if (number === previous! + 1) {
      previous = number;
      continue;
    }
    ranges.push(start === previous ? String(start) : `${start}–${previous}`);
    start = number;
    previous = number;
  }
  if (start !== undefined) ranges.push(start === previous ? String(start) : `${start}–${previous}`);
  return ranges.join(', ');
}

export function formatElapsed(ms: number): string {
  const seconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(seconds / 60);
  return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
}

/** True when the current state approves this lesson at the given revision. */
export function isLessonApproved(view: BuildView, lessonId: string, revision: number): boolean {
  return view.workingState.review.approvals.some((approval) =>
    approval.scope === 'lesson' && approval.targetId === lessonId && approval.targetRevision === revision);
}

export function isCourseApproved(view: BuildView, outlineId: string, revision: number): boolean {
  return view.workingState.review.approvals.some((approval) =>
    approval.scope === 'course' && approval.targetId === outlineId && approval.targetRevision === revision);
}

export function assumedFields(view: BuildView): BriefField[] {
  return BRIEF_FIELDS.filter((field) => view.workingState.brief[field].status === 'assumed');
}

/** Coverage gaps worded for the hint under the latest outline box. */
export function approvalHintItems(view: BuildView): string[] {
  const outline = view.workingState.outline;
  if (!outline || view.coverage.complete) return [];
  return view.coverage.missing.flatMap((gap) => {
    if (gap.code === 'course_approval_required') return [OUTLINE_COPY.lessonList];
    if (gap.code === 'lesson_approval_required') {
      const lesson = outline.lessons.find((candidate) => candidate.id === gap.targetId);
      return lesson ? [lesson.title] : [];
    }
    return [];
  });
}

export function latestAssistantMessage(messages: readonly MessageView[]): MessageView | null {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    if (messages[index]!.role === 'assistant') return messages[index]!;
  }
  return null;
}

export const suggestionsOf = (message: MessageView | null): string[] =>
  message?.parts.flatMap((part) => (isPart(part, 'suggestions') ? part.items : [])) ?? [];

export const taxonomyOptionsOf = (message: MessageView | null): TaxonomyOptionsPart | null =>
  message?.parts.find((part): part is TaxonomyOptionsPart => isPart(part, 'taxonomy_options')) ?? null;

export const conceptOptionsOf = (message: MessageView | null): ConceptOptionsPart | null =>
  message?.parts.find((part): part is ConceptOptionsPart => isPart(part, 'concept_options')) ?? null;

/** True while a "Lead with a video scenario" build has no picked video. */
export function isBrowsingForVideo(view: BuildView): boolean {
  return view.source.kind === 'video_browse' && view.workingState.choices.decisions.video?.status === 'pending';
}

/** True while an "Ask a question" build is still narrowing toward its anchor question. */
export function isBrowsingForAnchor(view: BuildView): boolean {
  return view.workingState.choices.browsing?.question !== undefined
    && view.workingState.choices.decisions.anchor_question?.status === 'pending';
}

/**
 * True when the snapshot shows what the build holds now: the same outline at
 * the same revision, or the same lesson at the same lesson revision. A reply
 * recovered after later input carries an older snapshot that cannot be approved.
 */
export function isSnapshotCurrent(view: BuildView, snapshot: OutlineSnapshotPart): boolean {
  const outline = view.workingState.outline;
  if (!outline || outline.id !== snapshot.outline.id) return false;
  const target = snapshotTarget(snapshot);
  if (target.kind === 'course') return outline.revision === target.outline.revision;
  return outline.lessons.some((lesson) => lesson.id === target.lesson.id && lesson.revision === target.lesson.revision);
}

/** The approval the "Looks good" chip submits for a snapshot, at the shown revision. */
export type BoxApproval =
  | { kind: 'lesson'; action: BuildAction }
  | { kind: 'course'; detailed: BuildAction; delegated: BuildAction }
  | null;

export function boxApproval(view: BuildView, snapshot: OutlineSnapshotPart): BoxApproval {
  if (!isSnapshotCurrent(view, snapshot)) return null;
  const target = snapshotTarget(snapshot);
  if (target.kind === 'lesson') {
    const { lesson } = target;
    if (lesson.blocks.length === 0 || isLessonApproved(view, lesson.id, lesson.revision)) return null;
    return {
      kind: 'lesson',
      action: { type: 'approve', scope: 'lesson', targetId: lesson.id, revision: lesson.revision, mode: 'detailed' },
    };
  }
  const { outline } = target;
  if (isCourseApproved(view, outline.id, outline.revision)) return null;
  const approve = (mode: 'detailed' | 'delegated'): BuildAction =>
    ({ type: 'approve', scope: 'course', targetId: outline.id, revision: outline.revision, mode });
  return { kind: 'course', detailed: approve('detailed'), delegated: approve('delegated') };
}

export function canStart(view: BuildView): boolean {
  return view.allowedActions.some((action) => action.type === 'start');
}

/**
 * The most recent snapshot that shows what the build holds now, from any
 * message. The approval chips bind to it, so the current plan stays
 * approvable when the latest reply carries no box or an older recovered one.
 */
export function currentSnapshot(view: BuildView): OutlineSnapshotPart | null {
  for (let index = view.messages.length - 1; index >= 0; index -= 1) {
    for (const part of view.messages[index]!.parts) {
      if (isPart(part, 'outline_snapshot') && isSnapshotCurrent(view, part)) return part;
    }
  }
  return null;
}

export type ChipSpec =
  | { kind: 'suggestion'; label: string }
  /** `primary` marks the chip that starts the build; it renders as the filled pill, last in the row. */
  | { kind: 'action'; label: string; action: BuildAction; primary?: boolean }
  /** A taxonomy option or Centerstage concept that was presented; submits the browse action for its position. */
  | { kind: 'option'; label: string; action: BuildAction }
  /** Opens the question picker over the whole bank (for the given decision), or the Centerstage video picker. */
  | { kind: 'picker'; label: string; picker: 'question' | 'video'; style: 'chip' | 'link'; decision?: 'anchor_question' | 'additional_questions' }
  | { kind: 'reveal-review-modes'; label: string };

const sameLabel = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * A suggestion that tells the agent to build ("Build it now", "Start
 * building"). Sent as text it would start generation through the agent, so
 * it is never offered: the fixed "Start building" chip is the one control.
 */
const startsBuild = (label: string): boolean => /\b(?:build\w*|generat\w*)\b/i.test(label);

/** True when the row already opens the picker for this decision. */
const hasPickerFor = (chips: readonly ChipSpec[], decision: DecisionKey): boolean => chips.some((chip) => chip.kind === 'picker'
  && (decision === 'video' ? chip.picker === 'video' : chip.picker === 'question' && chip.decision === decision));

/** True when the row already skips this decision. */
const hasSkipFor = (chips: readonly ChipSpec[], decision: DecisionKey): boolean => chips.some((chip) => chip.kind === 'action'
  && chip.action.type === 'skip' && chip.action.decision === decision);

/**
 * Chips under the latest assistant message: its taxonomy options (those with
 * tagged questions) or concepts (those with scenarios), the pairing chips,
 * a picker and skip chip for each pending decision the row does not already
 * answer, the model's suggestions, then chips derived from the build state,
 * ending with the primary "Start building" chip once the build can start. A
 * fixed chip replaces a suggestion with the same label, so "Looks good" is
 * always the approval and "Start building" is always the typed start action.
 */
export function deriveChips(view: BuildView, message: MessageView, options: { reviewModesRevealed: boolean }): ChipSpec[] {
  const optionChips: ChipSpec[] = [
    ...(taxonomyOptionsOf(message)?.options ?? []).filter((option) => option.questionCount > 0),
    ...(conceptOptionsOf(message)?.options ?? []).filter((option) => option.scenarioCount > 0),
  ].map((option) => ({ kind: 'option', label: option.label, action: { type: 'browse', position: option.position } }));
  const pairing: ChipSpec[] = [];
  const latest = latestAssistantMessage(view.messages)?.id === message.id;
  const questionLed = view.source.kind === 'question' || view.source.kind === 'question_browse';
  const videoLed = view.source.kind === 'video' || view.source.kind === 'video_browse';
  for (const part of latest ? message.parts : []) {
    if (isPart(part, 'question_suggestions') && (questionLed || videoLed)
      && view.workingState.choices.decisions.additional_questions?.status === 'pending') {
      const ids = part.items.map((item) => item.libraryId);
      const select = (libraryIds: string[]): BuildAction => ({ type: 'select', decision: 'additional_questions', libraryIds });
      pairing.push({ kind: 'action', label: ids.length === 3 ? CHIP_LABELS.addAllThree : ids.length === 2 ? CHIP_LABELS.addBoth : CHIP_LABELS.addIt, action: select(ids) });
      if (ids.length > 1) pairing.push({ kind: 'action', label: CHIP_LABELS.justFirst, action: select(ids.slice(0, 1)) });
      if (videoLed) pairing.push({ kind: 'picker', label: CHIP_LABELS.chooseOwn, picker: 'question', style: 'chip', decision: 'additional_questions' });
      else pairing.push({ kind: 'action', label: ids.length > 1 ? CHIP_LABELS.neither : CHIP_LABELS.noThanks, action: { type: 'skip', decision: 'additional_questions' } });
    }
    if (isPart(part, 'video_suggestion') && questionLed && view.workingState.choices.decisions.video?.status === 'pending') {
      pairing.push(
        { kind: 'action', label: CHIP_LABELS.useIt, action: { type: 'select', decision: 'video', libraryIds: [part.item.libraryId] } },
        { kind: 'suggestion', label: CHIP_LABELS.showAnother },
        { kind: 'action', label: CHIP_LABELS.skipVideo, action: { type: 'skip', decision: 'video' } },
      );
    }
  }
  const decisions: ChipSpec[] = [];
  for (const { decision, required } of latest ? pendingDecisions(view) : []) {
    if (!hasPickerFor(pairing, decision)) {
      decisions.push(decision === 'video'
        ? { kind: 'picker', label: DECISION_COPY.video.choose, picker: 'video', style: 'chip' }
        : { kind: 'picker', label: DECISION_COPY[decision].choose, picker: 'question', style: 'chip', decision });
    }
    if (!required && !hasSkipFor(pairing, decision)) {
      decisions.push({ kind: 'action', label: SKIP_CHIP_LABELS[decision], action: { type: 'skip', decision } });
    }
  }
  const fixed: ChipSpec[] = [];
  const snapshot = currentSnapshot(view);
  if (snapshot) {
    const approval = boxApproval(view, snapshot);
    if (approval?.kind === 'lesson') fixed.push({ kind: 'action', label: CHIP_LABELS.looksGood, action: approval.action });
    if (approval?.kind === 'course') {
      if (options.reviewModesRevealed) {
        fixed.push({ kind: 'action', label: CHIP_LABELS.reviewByLesson, action: approval.detailed });
        fixed.push({ kind: 'action', label: CHIP_LABELS.skipReview, action: approval.delegated });
      } else {
        fixed.push({ kind: 'reveal-review-modes', label: CHIP_LABELS.looksGood });
      }
    }
  }
  const outline = view.workingState.outline;
  if (outline && view.coverage.complete && view.coverage.mode === 'detailed' && outline.lessons.length > 1) {
    fixed.push({ kind: 'suggestion', label: CHIP_LABELS.reviewListAgain }, { kind: 'suggestion', label: CHIP_LABELS.backToLesson });
  }
  if (isBrowsingForAnchor(view)) fixed.push({ kind: 'picker', label: CHIP_LABELS.searchBank, picker: 'question', style: 'link' });
  if (isBrowsingForVideo(view)) fixed.push({ kind: 'picker', label: CHIP_LABELS.searchCenterstage, picker: 'video', style: 'link' });
  if (canStart(view)) fixed.push({ kind: 'action', label: CHIP_LABELS.start, action: { type: 'start' }, primary: true });
  const suggestions: ChipSpec[] = suggestionsOf(message)
    .filter((label) => !startsBuild(label) && ![...optionChips, ...pairing, ...decisions, ...fixed].some((chip) => sameLabel(chip.label, label)))
    .map((label) => ({ kind: 'suggestion', label }));
  return [...optionChips, ...pairing, ...decisions, ...suggestions, ...fixed];
}

export interface PendingDecision {
  decision: DecisionKey;
  required: boolean;
}

/**
 * Decisions still waiting for the author, in a stable order; each gets a
 * picker chip and, when optional, a skip chip unless the pairing chips
 * already answer it. While an "Ask a question" build is browsing, its anchor
 * is left out: the "search all questions" link after the chips opens the same
 * picker. A "Lead with a video scenario" build leaves its video out the same
 * way, for "search all scenarios".
 */
export function pendingDecisions(view: BuildView): PendingDecision[] {
  const { decisions } = view.workingState.choices;
  const viaChip: Partial<Record<DecisionKey, boolean>> = {
    anchor_question: isBrowsingForAnchor(view),
    video: isBrowsingForVideo(view),
  };
  return DECISION_KEYS.flatMap((decision) => {
    const entry = decisions[decision];
    if (!entry || entry.status !== 'pending' || viaChip[decision]) return [];
    return [{ decision, required: entry.required }];
  });
}

/** Lesson position (1-based) in the current outline, for approval lines. */
export function lessonPosition(view: BuildView, lessonId: string): number | null {
  const index = view.workingState.outline?.lessons.findIndex((lesson) => lesson.id === lessonId) ?? -1;
  return index >= 0 ? index + 1 : null;
}

/** The latest generation run, when it is the build's most recent generation. */
export function latestGenerationRun(view: BuildView) {
  return view.execution.recent.find((run) => run.kind === 'generation') ?? null;
}

/** The latest conversation run when it failed (and its retry option, if any). */
export function failedTurn(view: BuildView) {
  const run = view.execution.recent.find((candidate) => candidate.kind === 'conversation');
  if (!run || run.status !== 'failed') return null;
  const retry = view.allowedActions.find((action) => action.type === 'retry' && action.runId === run.id) ?? null;
  return { run, retry };
}
